import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CanView } from "../../../components/CanView/CanView"
import { EOrderCurrentStatus } from "../../../Enum/EOrderCurrentStatus"
import { Permissions } from "../../../Enum/PermissionsType"
import { OrderCatalogProduct } from "../../../Objects/Order/OrderCatalogProduct"

const useMinutesTimer = (startDate?: string) => {
  const [minutes, setMinutes] = useState(0)

  useEffect(() => {
    if (!startDate) {
      return
    }

    const update = () => setMinutes(Math.floor((Date.now() - new Date(startDate).getTime()) / 60000))

    update()

    const interval = setInterval(update, 60000)

    return () => clearInterval(interval)
  }, [startDate])

  return minutes
}

interface FulfillmentItemProps {
  item: OrderCatalogProduct
  isDelivery: boolean
  canShowActionButtons: boolean
  onUpdateStatus: (itemId: number, isNextStep: boolean) => void
}

export const FulfillmentItem: React.FC<FulfillmentItemProps> = ({
  item,
  isDelivery,
  canShowActionButtons,
  onUpdateStatus,
}) => {
  const { t } = useTranslation("fulfillment")

  const minutes = useMinutesTimer(item.date)

  const timerClass = minutes > 20 ? "text-red-600 animate-pulse font-bold" : minutes > 10 ? "text-orange-500 font-semibold" : "text-slate-500"

  return (
    <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50 p-4 transition hover:bg-white">
      <div className="mb-3 flex items-center justify-between">
        <span className="rounded-full bg-white px-3 py-1 text-xs font-bold uppercase ring-2 ring-red-300">
          {t(`item_statuses.${item.currentStatus}`)}
        </span>
        <span className={`text-sm ${timerClass}`}>{minutes} min</span>
      </div>

      <div className="font-bold text-slate-800">{item.getName()}</div>

      {item.addtionals.length > 0 && (
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-500">
          {item.addtionals.map((additional) => (
            <li key={additional.id}>+ {additional.addtionalEntity?.name} (x{additional.amount})</li>
          ))}
        </ul>
          )}

      {item.selections.length > 0 && (
        <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-indigo-600">
          {item.selections.map((selection, index) => (
            <li key={`${selection.selectionEntityId ?? "selection"}-${index}`}>
              {selection.selectionEntity?.name ?? t("selection")}: {selection.selectionOptionEntity?.name ?? t("option")}
            </li>
          ))}
        </ul>
          )}

      {item.description && <p className="mt-2 text-xs italic text-red-500">{t("note", { description: item.description })}</p>}

      <CanView permission={Permissions.CanEditeFulfillment}>
        {!item.isClosed() && canShowActionButtons && (
          <div className="mt-4 flex gap-2">
            {item.currentStatus !== EOrderCurrentStatus.Pending && item.needFulfillment() && (
              <button onClick={() => onUpdateStatus(item.id, false)} className="flex-1 rounded-lg bg-red-500 py-2 font-semibold text-white hover:bg-red-600" data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentItem.button.onUpdateStatusItemIdFalse">
                {t("back")}
              </button>
            )}
            {item.currentStatus !== EOrderCurrentStatus.Finished && (
              <button onClick={() => onUpdateStatus(item.id, true)} className="flex-1 rounded-lg bg-emerald-500 py-2 font-semibold text-white hover:bg-emerald-600" data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentItem.button.onUpdateStatusItemIdTrue">
                {item.currentStatus === EOrderCurrentStatus.Pending ? t("start") : t("ready")}
              </button>
            )}
            {!isDelivery && item.currentStatus === EOrderCurrentStatus.Finished && (
              <button onClick={() => onUpdateStatus(item.id, true)} className="flex-1 rounded-lg bg-emerald-500 py-2 font-semibold text-white hover:bg-emerald-600" data-interaction-description-key="interactionDescriptions:pages.fulfillmentPage.components.fulfillmentItem.button.onUpdateStatusItemIdTrue2">
                {t("delivered")}
              </button>
            )}
          </div>
        )}
      </CanView>
    </div>
  )
}
